import React, { useState, useEffect, useCallback } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Badge,
  Typography,
  Box,
} from '@mui/material';
import { ChatCircleText } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../../context/BusinessContext';
import { useThemeStyles } from '../../../theme/useThemeStyles';
import api from '../../../utils/api';
import DashboardCard from '../DashboardCard';

function UnreadMessagesCard({ dashboardContext }) {
  const navigate = useNavigate();
  const styles = useThemeStyles();
  const { selectedBusiness } = useBusiness();
  const [messages, setMessages] = useState({ conversations: [], totalCount: 0, loading: true, error: null });

  const businessId = selectedBusiness?.business?._id;

  const fetchMessages = useCallback(async () => {
    if (!businessId) return;

    try {
      setMessages(prev => ({ ...prev, loading: true, error: null }));
      const response = await api.get('/api/chat/conversations', {
        params: { businessId },
      });

      const conversations = Array.isArray(response.data) ? response.data : [];
      const isMyOverview = dashboardContext?.overviewScope === 'my';
      const userMatchIds = Array.isArray(dashboardContext?.userMatchIds)
        ? dashboardContext.userMatchIds.map((id) => String(id))
        : [];
      const unreadConversations = conversations.filter((conv) => {
        if ((conv.unreadCounts?.supplier || 0) <= 0) return false;
        if (!isMyOverview || userMatchIds.length === 0) return true;

        const participantIds = Array.isArray(conv.participants)
          ? conv.participants.flatMap((participant) => ([
              participant,
              participant?._id,
              participant?.id,
              participant?.user?._id,
              participant?.user?.id,
              participant?.member?._id,
              participant?.member?.id,
              participant?.staffMember?._id,
              participant?.staffMember?.id,
            ]).filter(Boolean))
          : [];

        const fallbackIds = [
          conv.assignedTo?._id,
          conv.assignedTo?.id,
          conv.assignedTo?.user?._id,
          conv.assignedTo?.user?.id,
          conv.staffMember?._id,
          conv.staffMember?.id,
          conv.member?._id,
          conv.member?.id,
        ].filter(Boolean);

        return [...participantIds, ...fallbackIds].some((id) => userMatchIds.includes(String(id)));
      });

      const totalCount = unreadConversations.reduce(
        (sum, conv) => sum + (conv.unreadCounts?.supplier || 0),
        0
      );

      setMessages({
        conversations: unreadConversations.slice(0, 5),
        totalCount,
        loading: false,
        error: null,
      });
    } catch (error) {
      setMessages({ conversations: [], totalCount: 0, loading: false, error: 'Failed to load messages' });
    }
  }, [businessId, dashboardContext?.overviewScope, dashboardContext?.userMatchIds]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return (
    <DashboardCard
      title="Unread Messages"
      icon={<ChatCircleText size={18} weight="regular" />}
      loading={messages.loading}
      error={messages.error}
      isEmpty={!messages.loading && messages.conversations.length === 0}
      emptyMessage="You are up to date. No unread messages. New replies from couples will appear here."
      emptyIcon={ChatCircleText}
      menuItems={[
        {
          label: 'View inbox',
          onClick: () => navigate('/supplier/messages'),
        },
      ]}
      iconBackgroundColor="rgba(128, 90, 213, 0.12)"
      iconColour="rgba(128, 90, 213, 1)"
      sx={{ height: '100%', minHeight: '400px' }}
    >
      {messages.conversations.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Badge badgeContent={messages.totalCount} color="error">
            <Typography variant="body2" color="text.secondary">
              Total unread messages
            </Typography>
          </Badge>
        </Box>
      )}
      {messages.conversations.length > 0 && <List dense>
        {messages.conversations.map((conversation, index) => (
          <ListItem
            key={conversation._id || index}
            sx={{ 
              px: 2, 
              py: 1.5,
              mb: 1,
              backgroundColor: '#ffffff',
              borderRadius: 1,
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/supplier/messages?conversation=${conversation._id}`);
            }}
          >
            <ListItemText
              primary={
                <Box sx={styles.layout.flexBetween}>
                  <Typography variant="body2" fontWeight={500}>
                    {conversation.title || 'Untitled Conversation'}
                  </Typography>
                  <Badge badgeContent={conversation.unreadCounts?.supplier || 0} color="error">
                    <Box />
                  </Badge>
                </Box>
              }
              secondary={
                conversation.messages && conversation.messages.length > 0 && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {conversation.messages[conversation.messages.length - 1]?.content?.substring(0, 50) || 'No messages'}
                  </Typography>
                )
              }
            />
          </ListItem>
        ))}
      </List>}
    </DashboardCard>
  );
}

export default UnreadMessagesCard;
